import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation, AnimatePresence } from 'motion/react';
import { Globe, ChevronLeft, Bell, Check, Loader2 } from 'lucide-react';
import { AUTH_COLORS } from '@/constants/theme';
import { useAuthStore } from '@/features/auth';
import { useUpdateProfile } from '@/features/users/hooks';
import { useAppName } from '@/hooks/useAppName';
import { platform } from '@/utils/platform';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { DEFAULT_AUTHENTICATED_ROUTE } from '@/config/routes';
import { switchLanguage } from '@/utils/langTransition';
import { triggerHapticFeedback } from '@/features/admin/utils/adminAnimations';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { http } from '@/network/http';
import { FloatingField } from '@/components/ui';
import { validateEnglishName, validateHebrewName, validatePhone } from '@/utils/nameValidation';

/* ── Constants ────────────────────────────────────────────── */

const jiggle = {
  jiggle: {
    x: [0, -6, 6, -4, 4, -2, 2, 0],
    transition: { duration: 0.4 },
  },
};

/* ── Themed style helpers ─────────────────────────────────── */

// Auth theme — reads from CSS variables set by ThemeProvider
const glass = {
  input: {
    backgroundColor: 'var(--auth-page-input-bg)',
    backdropFilter: 'blur(8px)',
    border: '1px solid var(--auth-page-input-border)',
    color: 'var(--auth-page-input-text)',
  },
  inputError: {
    backgroundColor: 'var(--auth-page-input-bg)',
    backdropFilter: 'blur(8px)',
    border: '1.5px solid #EF4444',
    color: 'var(--auth-page-input-text)',
  },
  heading: 'var(--auth-page-heading)',
  label: 'var(--auth-page-heading)',
  secondary: 'var(--auth-page-text)',
  sectionLabel: { color: 'var(--auth-page-text)', opacity: 0.7 },
  btn: {
    backgroundColor: 'var(--auth-page-button-bg)',
    color: 'var(--auth-page-button-text)',
    border: '1px solid var(--auth-page-card-border)',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  },
  btnOutline: (active: boolean) => active
    ? {
        backgroundColor: 'var(--auth-page-button-bg)',
        color: 'var(--auth-page-button-text)',
        border: '1px solid var(--auth-page-card-border)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
      }
    : {
        backgroundColor: 'var(--auth-page-input-bg)',
        backdropFilter: 'blur(8px)',
        color: 'var(--auth-page-heading)',
        border: '1px solid var(--auth-page-input-border)',
        boxShadow: 'none',
      },
  divider: 'var(--auth-page-card-border)',
  dot: (active: boolean) => ({
    backgroundColor: active ? 'var(--auth-page-accent)' : 'transparent',
    border: `2px solid ${active ? 'var(--auth-page-accent)' : 'var(--auth-page-card-border)'}`,
  }),
  eulaBox: {
    backgroundColor: 'var(--auth-page-input-bg)',
    backdropFilter: 'blur(8px)',
    border: '1px solid var(--auth-page-input-border)',
    color: 'var(--auth-page-heading)',
  },
  back: { color: 'var(--auth-page-text)', opacity: 0.7 },
  focusRing: 'focus:ring-blue-400/50',
};


/* ── Step Dots ─────────────────────────────────────────────── */

function StepDots({ currentStep, totalSteps, theme }: { currentStep: number; totalSteps: number; theme: { dot: (active: boolean) => Record<string, string> } }) {
  return (
    <div className="flex justify-center gap-2 py-3">
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        const isActive = step === currentStep;
        return (
          <motion.div
            key={step}
            animate={{ scale: isActive ? 1.25 : 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            className="rounded-full"
            style={{ width: 10, height: 10, ...theme.dot(isActive) }}
          />
        );
      })}
    </div>
  );
}

/* ── Slide Variants (RTL-aware) ───────────────────────────── */

function useSlideVariants() {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  return {
    enter: (dir: number) => ({
      x: dir * (isRtl ? -300 : 300),
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({
      x: dir * (isRtl ? 300 : -300),
      opacity: 0,
    }),
  };
}

/* ── Main Component ───────────────────────────────────────── */

export default function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const updateProfile = useUpdateProfile();
  const appName = useAppName();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const isRtl = i18n.dir() === 'rtl';
  const slideVariants = useSlideVariants();
  const { enableNotifications, checkPushGranted } = usePushNotifications();

  const theme = glass;

  // Determine starting step from DB state
  const initialStep = (!user?.firstNameHe || !user?.lastNameHe || !user?.phone) ? 1 : 2;

  const [step, setStep] = useState(initialStep);
  const [direction, setDirection] = useState(1);

  // Step 1 — Profile fields
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [firstNameHe, setFirstNameHe] = useState(user?.firstNameHe || '');
  const [lastNameHe, setLastNameHe] = useState(user?.lastNameHe || '');
  const [phone, setPhone] = useState(user?.phone || '');

  // Step 2 — Preferences
  const [preferredLanguage, setPreferredLanguage] = useState<'he' | 'en'>(
    i18n.language.startsWith('en') ? 'en' : 'he'
  );
  const [pushChoice, setPushChoice] = useState<'enabled' | null>(null);
  const [pushRequired, setPushRequired] = useState(false);
  const [pushChecking, setPushChecking] = useState(false);
  const bellControls = useAnimation();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [shakeField, setShakeField] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const firstNameEnControls = useAnimation();
  const lastNameEnControls = useAnimation();
  const firstNameHeControls = useAnimation();
  const lastNameHeControls = useAnimation();
  const phoneControls = useAnimation();

  const getInputStyle = (field: string) =>
    shakeField === field ? theme.inputError : theme.input;

  const shakeAndSet = (field: string, controls: ReturnType<typeof useAnimation>) => {
    triggerHapticFeedback();
    setShakeField(field);
    controls.start('jiggle');
    setTimeout(() => setShakeField(null), 1500);
  };

  const getFieldError = (field: string): string | null => {
    if (field === 'firstNameEn' || field === 'lastNameEn') {
      const val = field === 'firstNameEn' ? firstName : lastName;
      const err = validateEnglishName(val);
      return err ? t(err) : null;
    }
    if (field === 'firstNameHe' || field === 'lastNameHe') {
      const val = field === 'firstNameHe' ? firstNameHe : lastNameHe;
      const err = validateHebrewName(val);
      return err ? t(err) : null;
    }
    if (field === 'phone') {
      const err = validatePhone(phone);
      return err ? t(err) : null;
    }
    return null;
  };

  /* ── Navigation helpers ─────────────────────────────────── */

  const goToStep = (nextStep: number, dir: number) => {
    setDirection(dir);
    setStep(nextStep);
  };

  /* ── Step 1: Save profile ───────────────────────────────── */

  const handleStep1Submit = async () => {
    setError(null);

    const fields: [string, ReturnType<typeof useAnimation>][] = [
      ['firstNameEn', firstNameEnControls],
      ['lastNameEn', lastNameEnControls],
      ['firstNameHe', firstNameHeControls],
      ['lastNameHe', lastNameHeControls],
      ['phone', phoneControls],
    ];

    for (const [field, controls] of fields) {
      const err = getFieldError(field);
      if (err) {
        shakeAndSet(field, controls);
        setError(err);
        return;
      }
    }

    setLoading(true);
    try {
      await updateProfile.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        firstNameHe: firstNameHe.trim(),
        lastNameHe: lastNameHe.trim(),
        phone: phone.trim(),
      });

      if (user) {
        setUser({
          ...user,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          firstNameHe: firstNameHe.trim(),
          lastNameHe: lastNameHe.trim(),
          phone: phone.trim(),
        });
      }

      goToStep(2, 1);
    } catch {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  /** Scan push permission up to `attempts` times with a delay between each. */
  const scanPushPermission = async (attempts = 3, delayMs = 600): Promise<boolean> => {
    for (let i = 0; i < attempts; i++) {
      const granted = await checkPushGranted();
      if (granted) return true;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
    }
    return false;
  };

  const jiggleBell = () => {
    triggerHapticFeedback();
    setPushRequired(true);
    setPushChoice(null);
    bellControls.start({
      rotate: [0, 20, -20, 15, -15, 10, -10, 5, -5, 0],
      transition: { duration: 0.6 },
    });
  };

  /* ── Step 2: Advance to welcome ───────────────────────── */

  const handleStep2Next = async () => {
    setPushChecking(true);
    const granted = await scanPushPermission();
    setPushChecking(false);
    if (!granted) {
      jiggleBell();
      return;
    }
    setPushChoice('enabled');
    setPushRequired(false);
    goToStep(3, 1);
  };

  /* ── Step 3: Finalize — save to API + redirect ──────────── */

  const handleGetStarted = async () => {
    setLoading(true);
    try {
      await updateProfile.mutateAsync({ preferredLanguage });

      // Accept terms as part of onboarding completion
      const termsRes = await http.post('/users/me/accept-terms');
      const termsAcceptedAt = termsRes.data?.termsAcceptedAt || new Date().toISOString();

      if (user) {
        setUser({ ...user, preferredLanguage, termsAcceptedAt });
      }

      if (preferredLanguage !== i18n.language) {
        await switchLanguage(i18n, preferredLanguage, false);
      }

      navigate(DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
    } catch {
      setError(t('common.error'));
      setLoading(false);
    }
  };

  /* ── Push notification handlers ─────────────────────────── */

  const handleEnablePush = async () => {
    setPushChecking(true);
    await enableNotifications();
    const granted = await scanPushPermission();
    setPushChecking(false);
    if (granted) {
      setPushChoice('enabled');
      setPushRequired(false);
    } else {
      jiggleBell();
    }
  };

  /* ── Back Button ────────────────────────────────────────── */

  const BackButton = () => (
    <button
      type="button"
      dir="ltr"
      onClick={() => goToStep(step - 1, -1)}
      className="absolute top-0 flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer"
      style={{ ...theme.back, left: 0 }}
    >
      <ChevronLeft size={18} />
      {t('onboarding.back')}
    </button>
  );

  /* ── Logo ────────────────────────────────────────────────── */

  const Logo = () => (
    <div
      className="mx-auto h-20 aspect-[3/2] mb-2"
      role="img"
      aria-label={appName}
      style={{
        background: 'linear-gradient(145deg, var(--auth-page-heading) 0%, var(--auth-page-accent) 45%, var(--auth-page-heading) 55%, var(--auth-page-accent) 100%)',
        maskImage: 'url(/images/vidnight-logo-white.png)',
        WebkitMaskImage: 'url(/images/vidnight-logo-white.png)',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))',
      }}
    />
  );

  /* ── Step 1: Profile Form ───────────────────────────────── */

  const step1Content = (
    <div className="space-y-4">

      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: theme.heading }}>{t('onboarding.title')}</h1>
        <p className="mt-2 text-sm font-medium" style={{ color: theme.secondary, opacity: 0.7 }}>
          {t('onboarding.subtitle')}
        </p>
      </div>

      {/* Error */}
      {error && step === 1 && (
        <div
          className="rounded-xl p-3 text-sm font-bold"
          style={{
            backgroundColor: AUTH_COLORS.errorBg,
            border: '1px solid var(--auth-page-card-border)',
            color: AUTH_COLORS.errorText,
          }}
        >
          {error}
        </div>
      )}

      {/* Name in English */}
      <p className="text-xs font-bold uppercase tracking-wider" style={theme.sectionLabel}>
        {t('auth.nameInEnglish')}
      </p>
      <div className="grid grid-cols-2 gap-3" dir="ltr">
        <motion.div variants={jiggle} animate={firstNameEnControls}>
          <FloatingField
            inputId="ob-firstName"
            label={t('auth.firstNameEn')}
            type="text"
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); if (shakeField === 'firstNameEn') setShakeField(null); }}
            inputClassName={`focus:ring-2 ${theme.focusRing}`}
            inputStyle={getInputStyle('firstNameEn')}
            inputDir="ltr"
          />
          {shakeField === 'firstNameEn' && (
            <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-medium mt-1" style={{ color: '#EF4444' }}>
              {getFieldError('firstNameEn')}
            </motion.p>
          )}
        </motion.div>
        <motion.div variants={jiggle} animate={lastNameEnControls}>
          <FloatingField
            inputId="ob-lastName"
            label={t('auth.lastNameEn')}
            type="text"
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); if (shakeField === 'lastNameEn') setShakeField(null); }}
            inputClassName={`focus:ring-2 ${theme.focusRing}`}
            inputStyle={getInputStyle('lastNameEn')}
            inputDir="ltr"
          />
          {shakeField === 'lastNameEn' && (
            <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-medium mt-1" style={{ color: '#EF4444' }}>
              {getFieldError('lastNameEn')}
            </motion.p>
          )}
        </motion.div>
      </div>

      {/* Name in Hebrew */}
      <p className="text-xs font-bold uppercase tracking-wider" style={theme.sectionLabel}>
        {t('auth.nameInHebrew')}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <motion.div variants={jiggle} animate={firstNameHeControls}>
          <FloatingField
            inputId="ob-firstNameHe"
            label={t('auth.firstNameHe')}
            type="text"
            value={firstNameHe}
            onChange={(e) => { setFirstNameHe(e.target.value); if (shakeField === 'firstNameHe') setShakeField(null); }}
            inputClassName={`focus:ring-2 ${theme.focusRing}`}
            inputStyle={getInputStyle('firstNameHe')}
            inputDir="rtl"
          />
          {shakeField === 'firstNameHe' && (
            <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-medium mt-1" style={{ color: '#EF4444' }}>
              {getFieldError('firstNameHe')}
            </motion.p>
          )}
        </motion.div>
        <motion.div variants={jiggle} animate={lastNameHeControls}>
          <FloatingField
            inputId="ob-lastNameHe"
            label={t('auth.lastNameHe')}
            type="text"
            value={lastNameHe}
            onChange={(e) => { setLastNameHe(e.target.value); if (shakeField === 'lastNameHe') setShakeField(null); }}
            inputClassName={`focus:ring-2 ${theme.focusRing}`}
            inputStyle={getInputStyle('lastNameHe')}
            inputDir="rtl"
          />
          {shakeField === 'lastNameHe' && (
            <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-medium mt-1" style={{ color: '#EF4444' }}>
              {getFieldError('lastNameHe')}
            </motion.p>
          )}
        </motion.div>
      </div>

      {/* Phone */}
      <motion.div variants={jiggle} animate={phoneControls} dir="ltr">
        <FloatingField
          inputId="ob-phone"
          label={t('register.phone')}
          type="tel"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); if (shakeField === 'phone') setShakeField(null); }}
          inputClassName={`focus:ring-2 ${theme.focusRing}`}
          inputStyle={getInputStyle('phone')}
          inputDir="ltr"
        />
        {shakeField === 'phone' && (
          <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-medium mt-1" style={{ color: '#EF4444' }}>
            {getFieldError('phone')}
          </motion.p>
        )}
      </motion.div>

      {/* Next button */}
      <button
        type="button"
        onClick={handleStep1Submit}
        disabled={loading}
        className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        style={theme.btn}
      >
        {loading ? t('common.loading') : t('onboarding.next')}
      </button>
    </div>
  );

  /* ── Step 2: Language + Push ────────────────────────────── */

  const step2Content = (
    <div className="space-y-5 relative">

      <BackButton />

      {/* Header */}
      <div className="text-center pt-6">
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: theme.heading }}>{t('onboarding.preferencesTitle')}</h1>
        <p className="mt-2 text-sm font-medium" style={{ color: theme.secondary, opacity: 0.7 }}>
          {t('onboarding.preferencesSubtitle')}
        </p>
      </div>

      {/* Error */}
      {error && step === 2 && (
        <div
          className="rounded-xl p-3 text-sm font-bold"
          style={{
            backgroundColor: AUTH_COLORS.errorBg,
            border: '1px solid var(--auth-page-card-border)',
            color: AUTH_COLORS.errorText,
          }}
        >
          {error}
        </div>
      )}

      {/* Section A — Language */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wider mb-2" style={theme.sectionLabel}>
          {t('onboarding.preferredLanguage')}
        </p>
        <div className="flex gap-3">
          {(['he', 'en'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => {
                setPreferredLanguage(lang);
                switchLanguage(i18n, lang, false);
              }}
              className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] cursor-pointer"
              style={theme.btnOutline(preferredLanguage === lang)}
            >
              <Globe size={16} />
              {lang === 'he' ? 'עברית' : 'English'}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t" style={{ borderColor: theme.divider, opacity: 0.5 }} />

      {/* Section B — Push Notifications */}
      <div className="text-center space-y-3">
        {/* Animated Bell */}
        <motion.div animate={bellControls} className="inline-block">
          <Bell size={48} style={{ color: pushRequired ? '#EF4444' : theme.heading }} />
        </motion.div>

        <h2 className="text-lg font-bold" style={{ color: theme.heading }}>{t('onboarding.pushTitle')}</h2>
        <p className="text-sm" style={{ color: theme.secondary, opacity: 0.7 }}>
          {t('onboarding.pushDescription')}
        </p>

        {pushChoice !== 'enabled' ? (
          <>
            <button
              type="button"
              onClick={handleEnablePush}
              disabled={pushChecking}
              className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-70 cursor-pointer flex items-center justify-center gap-2"
              style={theme.btn}
            >
              {pushChecking ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                t('onboarding.pushEnable')
              )}
            </button>
            {pushRequired && !pushChecking && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs font-medium"
                style={{ color: '#EF4444' }}
              >
                {t('onboarding.pushRequired')}
              </motion.p>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center justify-center gap-2 py-3 rounded-xl"
            style={{
              backgroundColor: '#ECFDF5',
              border: '1px solid #A7F3D0',
              color: '#065F46',
            }}
          >
            <Check size={16} />
            <span className="text-sm font-medium">
              {t('onboarding.pushEnabled')}
            </span>
          </motion.div>
        )}
      </div>

      {/* Next button */}
      <button
        type="button"
        onClick={handleStep2Next}
        disabled={loading || pushChecking}
        className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
        style={theme.btn}
      >
        {pushChecking ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          loading ? t('common.loading') : t('onboarding.next')
        )}
      </button>
    </div>
  );

  /* ── Step 3: Welcome + Terms ─────────────────────────────── */

  const step3Content = (
    <div className="space-y-4 relative">

      <BackButton />

      {/* Header */}
      <div className="text-center pt-6">
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: theme.heading }}>{t('terms.title')}</h1>
        <p className="mt-1 text-sm font-medium" style={{ color: theme.secondary, opacity: 0.7 }}>
          {t('terms.subtitle')}
        </p>
      </div>

      {/* Error */}
      {error && step === 3 && (
        <div
          className="rounded-xl p-3 text-sm font-bold"
          style={{
            backgroundColor: AUTH_COLORS.errorBg,
            border: '1px solid var(--auth-page-card-border)',
            color: AUTH_COLORS.errorText,
          }}
        >
          {error}
        </div>
      )}

      {/* EULA scrollable area */}
      <div
        className="rounded-xl overflow-y-scroll text-xs leading-relaxed eula-scroll"
        style={{
          height: isMobile ? 220 : 200,
          padding: '14px 16px',
          backgroundColor: 'var(--auth-page-input-bg)',
          border: '1px solid var(--auth-page-input-border)',
          color: 'var(--auth-page-heading)',
        }}
      >
        <h3 className="font-bold mb-2 text-sm">{t('terms.eulaTitle')}</h3>
        <p className="mb-3">{t('terms.eulaIntro')}</p>
        <h4 className="font-semibold mt-3 mb-1">{t('terms.conductTitle')}</h4>
        <ul className="list-disc ps-5 space-y-1">
          <li>{t('terms.conductRule1')}</li>
          <li>{t('terms.conductRule2')}</li>
          <li>{t('terms.conductRule3')}</li>
          <li>{t('terms.conductRule4')}</li>
        </ul>
        <h4 className="font-semibold mt-3 mb-1">{t('terms.moderationTitle')}</h4>
        <p>{t('terms.moderationText')}</p>
        <h4 className="font-semibold mt-3 mb-1">{t('terms.terminationTitle')}</h4>
        <p>{t('terms.terminationText')}</p>
      </div>

      {/* Checkbox */}
      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-blue-200 shrink-0"
        />
        <span className="text-sm font-medium" style={{ color: theme.heading }}>
          {t('terms.acceptCheckbox')}
        </span>
      </label>

      {/* Get Started button */}
      <button
        type="button"
        onClick={handleGetStarted}
        disabled={loading || !termsAccepted}
        className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        style={theme.btn}
      >
        {loading ? t('common.loading') : t('onboarding.getStarted')}
      </button>
    </div>
  );

  /* ── Card wrapper shared by each step ─────────────────── */

  const glassCardStyle = {
    backgroundColor: 'var(--auth-page-card-bg)',
    backdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid var(--auth-page-card-border)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
  };

  const stepCard = (key: string, content: React.ReactNode, padding: string) => (
    <motion.div
      key={key}
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`space-y-4 ${isMobile ? padding : `rounded-3xl ${padding}`}`}
      style={isMobile ? undefined : glassCardStyle}
    >
      {/* Logo + Dots */}
      <div className="text-center">
        <Logo />
        <StepDots currentStep={step} totalSteps={3} theme={theme} />
      </div>
      {content}
    </motion.div>
  );

  /* ── Layout (mobile vs desktop) ─────────────────────────── */

  const wizardAnimated = (padding: string) => (
    <AnimatePresence mode="wait" custom={direction}>
      {step === 1 && stepCard('step1', step1Content, padding)}
      {step === 2 && stepCard('step2', step2Content, padding)}
      {step === 3 && stepCard('step3', step3Content, padding)}
    </AnimatePresence>
  );

  if (isMobile) {
    return (
      <div className="login-mesh-bg fixed inset-0" style={{ position: 'fixed' }}>
        <div
          className="relative z-10 h-full overflow-y-auto flex flex-col justify-center px-5 py-8 space-y-5"
          style={{
            paddingTop: platform.isAndroid ? '2rem' : 'calc(env(safe-area-inset-top, 0px) + 2rem)',
            backgroundColor: 'var(--auth-page-bg)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {wizardAnimated('p-2')}
        </div>
      </div>
    );
  }

  return (
    <div className="login-mesh-bg fixed inset-0 flex items-center justify-center" style={{ position: 'fixed' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
      >
        {wizardAnimated('p-8')}
      </motion.div>
    </div>
  );
}
